import { ethers } from 'ethers';
import { encodeFunctionData } from 'viem';

import useWrappedRPC from './useWrappedRPC';
import useToken from './useToken';
import { abiPaymentGateway } from '../config/abi.contract';
import {
  getGoldPurchaseSignature,
  validateGoldPurchaseTransaction,
  getPackPurchaseSignature,
  validatePackPurchaseTransaction,
  getDivingTicketPurchaseSignature,
  validateDivingTicketPurchaseTransaction,
  getLunarBaitPurchaseSignature,
  validateLunarBaitPurchaseTransaction,
  getGuildBaitPurchaseSignature,
  validateGuildBaitPurchaseTransaction,
  getCrownBaitPurchaseSignature,
  validateCrownBaitPurchaseTransaction,
  trackAnalyticsEventToBackend,
} from '../services/transaction.service';
import environments from '../utils/environments';
import useUserStore from '../stores/user.store';

const {
  FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
  FISH_TOKEN_CONTRACT_ADDRESS,
  XFISH_TOKEN_CONTRACT_ADDRESS,
  USDC_ADDRESS,
} = environments;

export const tokens = {
  RON: ethers.constants.AddressZero,
  FISH: FISH_TOKEN_CONTRACT_ADDRESS,
  xFISH: XFISH_TOKEN_CONTRACT_ADDRESS,
  USDC: USDC_ADDRESS,
};

const tokenToCurrencyMap = Object.fromEntries(Object.entries(tokens).map(([key, value]) => [value, key]));

function usePurchase() {
  const { checkAndApproveToken, getUSDCBalance } = useToken();
  const { sendTransactionWithGas } = useWrappedRPC();
  const user = useUserStore((state) => state.user);

  const trackPurchaseEvent = async (value, currency, category, tokenAddress) => {
    try {
      const numericValue = Number(value);
      let usdPrice = 0;

      if (currency === 'USDC') {
        usdPrice = 1;
      } else if (currency === 'RON') {
        const res = await fetch('https://exchange-rate.skymavis.com/v2/prices/ron');
        const data = await res.json();
        usdPrice = data.result?.usd || 0;
      } else if (tokenAddress && tokenAddress !== ethers.constants.AddressZero) {
        const lookupAddress = currency === 'xFISH' ? tokens.FISH : tokenAddress;
        const res = await fetch(`https://exchange-rate.skymavis.com/v2/prices?addresses=${lookupAddress}`);
        const data = await res.json();
        usdPrice = data.result?.[lookupAddress.toLowerCase()]?.usd || 0;
      }

      const usdValue = Number((numericValue * usdPrice).toFixed(2));

      if (typeof window !== 'undefined' && usdValue > 0) {
        // Retrieve userId from localStorage, default to empty string if missing
        // as per requirements to ensure the field is always present in the payload.
        const userId = localStorage.getItem('userId') || '';

        let ff_attribution = null;
        let ff_attribution_new = null;
        try {
          const storedOld = localStorage.getItem('ff_attribution');
          if (storedOld) ff_attribution = JSON.parse(storedOld);
        } catch (e) {
          console.error('Failed to parse ff_attribution', e);
        }
        try {
          const storedNew = localStorage.getItem('ff_attribution_new');
          if (storedNew) ff_attribution_new = JSON.parse(storedNew);
        } catch (e) {
          console.error('Failed to parse ff_attribution_new', e);
        }

        const backendPayloadBase = {
          usdValue,
          currency: 'USD',
          content_type: 'product',
          content_name: category,
          category,
          userId,
          attribution: {
            old: ff_attribution,
            new: ff_attribution_new,
          },
        };

        // Fire backend events unconditionally to capture conversions even if adblockers block frontend pixels
        trackAnalyticsEventToBackend({ ...backendPayloadBase, platform: 'facebook' }).catch(console.error);
        trackAnalyticsEventToBackend({ ...backendPayloadBase, platform: 'reddit' }).catch(console.error);

        if (typeof window.fbq === 'function') {
          const payload = {
            value: usdValue,
            currency: 'USD',
            content_type: 'product',
            content_name: category,
            category,
            userId,
          };
          window.fbq('track', 'Purchase', payload);
        }
        if (typeof window.rdt === 'function') {
          const payload = {
            value: usdValue,
            currency: 'USD',
            category,
            userId,
          };
          window.rdt('track', 'Purchase', payload);
        }
      }
    } catch (error) {
      console.error('Error tracking purchase event:', error);
    }
  };

  const purchaseGold = async (_amount) => {
    try {
      // default is USDC
      // const paymentTokenFE = ethers.constants.AddressZero;
      // fbq('track', 'Purchase', { value: _amount, currency: 'RON', category: 'test' });
      const paymentTokenFE = USDC_ADDRESS;
      const res = await getGoldPurchaseSignature({ amount: _amount, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const decimals = 6;
      const parsedAmount = ethers.utils.parseUnits(`${amount}`, decimals);

      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
        value: parsedAmount,
      };

      let tokenString;
      for (let key of Object.keys(tokens)) {
        if (tokens[key].toLowerCase() === paymentToken.toLowerCase()) tokenString = key;
      }
      const txUiConfig = {
        header: `Purchase Gold`,
        description: `Send ${amount} ${tokenString} to purchase gold`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateGoldPurchaseTransaction({ transactionHash: txReceipt.hash });
      trackPurchaseEvent(_amount, 'RON', 'Purchase-Gold', paymentTokenFE);
      return {
        success: true,
        message: 'Purchase gold successful',
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase gold transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchasePack = async (_amount, rawPackId, packName, token) => {
    try {
      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getPackPurchaseSignature({ packId: rawPackId, paymentToken: paymentTokenFE });
      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        quantity,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      // const parsedAmount = ethers.utils.parseEther(`${amount}`);

      const decimals = paymentTokenFE === tokens.USDC ? 6 : 18;
      const parsedAmount = ethers.utils.parseUnits(`${amount}`, decimals);

      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
          tokenDecimals: decimals,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase ${packName}`,
        description: `Send ${amount} ${token} to purchase ${packName}`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validatePackPurchaseTransaction({ transactionHash: txReceipt.hash, packId: rawPackId });
      const currency = tokenToCurrencyMap[paymentTokenFE] ?? 'RON';
      trackPurchaseEvent(_amount, currency, 'Purchase-Package', paymentTokenFE);
      return {
        success: true,
        message: `Purchase ${packName} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase starter pack transaction failed:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseDivingTicket = async (quantity, token) => {
    try {
      const paymentTokenFE = tokens[token] || FISH_TOKEN_CONTRACT_ADDRESS;
      const res = await getDivingTicketPurchaseSignature({ quantity, paymentToken: paymentTokenFE });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const decimals = paymentTokenFE === tokens.USDC ? 6 : 18;
      const parsedAmount = ethers.utils.parseUnits(`${amount}`, decimals);

      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
          tokenDecimals: decimals,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase diving ticket`,
        description: `Send ${amount} ${token} to purchase a diving ticket`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const divingTypeMap = {
        RON: 'Premium',
        FISH: 'Token',
        xFISH: 'Token',
        USDC: 'USDC',
      };
      const type = divingTypeMap[token] || 'Token';
      const res1 = await validateDivingTicketPurchaseTransaction({ transactionHash: txReceipt.hash, quantity, type });
      const currency = tokenToCurrencyMap[paymentTokenFE] ?? 'RON';
      trackPurchaseEvent(amount, currency, 'Purchase-DivingTicket', paymentTokenFE);
      return {
        success: true,
        message: res1.data.result.message,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase diving ticket:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseLunarBait = async (quantity) => {
    try {
      const res = await getLunarBaitPurchaseSignature({ quantity });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase lunar baits`,
        description: `Send ${amount} FISH to purchase ${quantity} lunar baits`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateLunarBaitPurchaseTransaction({ transactionHash: txReceipt.hash });

      const currency = tokenToCurrencyMap[paymentToken] ?? 'RON';
      trackPurchaseEvent(amount, currency, 'Purchase-LunarBait', paymentToken);
      return {
        success: true,
        message: `Purchase ${quantity} Lunar Bait${quantity > 1 ? 's' : ''} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase lunar bait:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseGuildBait = async (quantity) => {
    try {
      const res = await getGuildBaitPurchaseSignature({ quantity });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const decimals = paymentToken === tokens.USDC ? 6 : 18;
      const parsedAmount = ethers.utils.parseUnits(String(amount).trim(), decimals);

      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount: parsedAmount,
        });
      else transactionRequest.value = parsedAmount;

      const currency = tokenToCurrencyMap[paymentToken] ?? 'RON';

      const txUiConfig = {
        header: `Purchase lunar baits`,
        description: `Send ${amount} ${currency} to purchase ${quantity} lunar baits`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateGuildBaitPurchaseTransaction({ transactionHash: txReceipt.hash });
      const updatedBalance = await getUSDCBalance(user.walletAddress);

      trackPurchaseEvent(amount, currency, 'Purchase-GuildBait', paymentToken);
      return {
        success: true,
        message: `Purchase ${quantity} Guild Bait${quantity > 1 ? 's' : ''} successful`,
        data: {
          ...res1.data,
          usdcBalance: updatedBalance,
        },
      };
    } catch (error) {
      console.error('Purchase guild bait:', error);
      return { success: false, message: error.message };
    }
  };

  const purchaseCrownBait = async (quantity) => {
    try {
      const res = await getCrownBaitPurchaseSignature({ quantity });

      const {
        rawAmount: amount,
        purchaseType,
        paymentToken,
        packId,
        nonce,
        deadline,
        refType,
        referrer,
        signature,
      } = res.data;

      const parsedAmount = ethers.utils.parseEther(`${amount}`);
      const data = encodeFunctionData({
        abi: abiPaymentGateway,
        functionName: 'purchase',
        args: [
          purchaseType,
          paymentToken,
          parsedAmount,
          packId,
          quantity,
          nonce,
          deadline,
          refType,
          referrer,
          signature,
        ],
      });

      const isNativeToken = paymentToken === ethers.constants.AddressZero;

      const transactionRequest = {
        to: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
        data,
      };
      if (!isNativeToken)
        await checkAndApproveToken({
          tokenAddress: paymentToken,
          ownerAddress: user.walletAddress,
          spenderAddress: FISHING_FRENZY_PURCHASE_GATEWAY_CONTRACT_ADDRESS,
          amount,
        });
      else transactionRequest.value = parsedAmount;

      const txUiConfig = {
        header: `Purchase lunar baits`,
        description: `Send ${amount} FISH to purchase ${quantity} lunar baits`,
        buttonText: `Send`,
      };

      const { txReceipt } = await sendTransactionWithGas(transactionRequest, txUiConfig);
      if (!txReceipt) throw new Error('Transaction failed on blockchain');

      const res1 = await validateCrownBaitPurchaseTransaction({ transactionHash: txReceipt.hash });

      const currency = tokenToCurrencyMap[paymentToken] ?? 'RON';
      trackPurchaseEvent(amount, currency, 'Purchase-CrownBait', paymentToken);
      return {
        success: true,
        message: `Purchase ${quantity} Crown Bait${quantity > 1 ? 's' : ''} successful`,
        data: res1.data,
      };
    } catch (error) {
      console.error('Purchase crown bait:', error);
      return { success: false, message: error.message };
    }
  };

  return { purchaseGold, purchasePack, purchaseDivingTicket, purchaseLunarBait, purchaseGuildBait, purchaseCrownBait };
}

export default usePurchase;
